import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { downloadFileWithCheck } from '../../__legacy__/helpers/fileDownload.helpers';
import { ACDLError, ForbiddenError, InputStatus } from '../../__legacy__/models/error.model';
import { NotificationLevel, ToastNotification } from '../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../__legacy__/modules/notifications/services/notifications.service';
import { IOverlayLoaderStore } from '../common/OverlayLoader.store';
import { IInferenceListItemDto, ITrainingListItemDto } from '../jobs/jobs.api.model';
import { IInferenceListItem, IJobDownload, ITrainingListItem, JOB_DOWNLOAD_TYPE, JOB_OWNERSHIP, JOB_STATUS, JOB_TYPE, TRAINING_TYPE } from '../jobs/jobs.model';
import { IJobsStore } from '../jobs/jobs.store';
import { IJobsApiService } from '../jobs/services/jobsApi.service';
import { ICurrentWorkspaceStore } from '../workspaces/currentWorkspace/CurrentWorkspace.store';
import { IJobActionsApiService, IMPORT_ERROR_CODES } from './services/jobActionsApi.service';
import { IJobDetailsApiService } from './services/jobDetailsApi.service';
import { AsyncGuardError, ensureNotStale, getJobTranslationPrefix } from './utils';

export interface IJobsService {
  store: IJobsStore;
  changeOrder(orderBy: string | undefined, orderType: string | undefined): void;
  changeOwnershipFilter(ownership: JOB_OWNERSHIP): void;
  changePagination(pageNumber: number, pageSize: number): void;
  downloadJobAsync: (jobId: string) => Promise<IJobDownload | null>;
  restartJobAsync(jobId: string): Promise<void>;
  stopJobAsync(jobId: string): Promise<void>;
  deleteJobAsync(jobId: string): Promise<void>;
  refreshAsync(showLoader: boolean): Promise<void>;
  initPageSize(initialPageSize: number): void;
  importModelsAsync(name: string, description: string, file: File): Promise<boolean>;
  getImportErrors: () => Map<string, InputStatus>;
  clearImportErrors(): void;
}

export namespace IJobsService {
  export const $: interfaces.ServiceIdentifier<IJobsService> = Symbol('JOBS_SERVICE');
}

@injectable()
export class JobsService implements IJobsService {
  constructor(
    @inject(IJobsStore.$) public readonly store: IJobsStore,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore,
    @inject(IJobsApiService.$) private readonly jobsApiService: IJobsApiService,
    @inject(IJobActionsApiService.$) private readonly jobActionsApiService: IJobActionsApiService,
    @inject(IJobDetailsApiService.$) private readonly jobDetailsApiService: IJobDetailsApiService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    makeObservable(this);
  }

  initPageSize(initialPageSize: number): void {
    this.store.paging.pageSize = initialPageSize;
    this.store.paging.pageNumber = 1;
  }

  async notifyAboutApiSuccess(code: string): Promise<void> {
    await this.notificationsService.push(
      new ToastNotification(NotificationLevel.SUCCESS, `${getJobTranslationPrefix(this.store.jobType)}.${code}` as TranslationKey)
    );
  }

  async notifyAboutApiError(result: unknown, errorCode: string, dontShowBadRequest: boolean = false): Promise<void> {
    if (
      result instanceof ACDLError &&
      !(result instanceof AsyncGuardError) &&
      !(result instanceof ForbiddenError) &&
      (!dontShowBadRequest || !result.isBadRequest())
    ) {
      await this.notificationsService.push(
        new ToastNotification(NotificationLevel.ERROR, `${getJobTranslationPrefix(this.store.jobType)}.${errorCode}` as TranslationKey)
      );
    }
  }

  wrapEnsureNotStale = async <T>(method: () => Promise<T>): Promise<T | ACDLError> => {
    return ensureNotStale(method, () => {
      const {
        storeVersion,
        paging: { orderBy, orderType, pageNumber },
        ownership,
      } = this.store;

      return `${storeVersion}-ws-${this.currentWorkspaceStore.currentWorkspace.id}-${orderBy}-${orderType}-${pageNumber}-${ownership}`;
    });
  };

  @action
  changeOwnershipFilter(ownership: JOB_OWNERSHIP): void {
    if (ownership !== this.store.ownership) {
      this.store.ownership = ownership;
      this.store.paging.pageNumber = 1;
    }
  }

  @action
  changeOrder(orderBy: string | undefined, orderType: string | undefined): void {
    this.store.paging.orderBy = orderBy ?? '';
    this.store.paging.orderType = orderType ?? '';
  }

  @action
  changePagination(pageNumber: number, pageSize: number): void {
    this.store.paging.pageNumber = pageNumber;
    this.store.paging.pageSize = pageSize;
  }

  @action
  async refreshAsync(showLoader: boolean = true): Promise<void> {
    const { currentWorkspace } = this.currentWorkspaceStore;

    if (showLoader) {
      this.overlayLoaderStore.enableLoader('jobs-list');
    }

    const fetchedSuccessfully = await this.getJobs(currentWorkspace.id);

    if (!fetchedSuccessfully) {
      return;
    }

    this.overlayLoaderStore.disableLoader('jobs-list');
  }

  @action
  async getJobs(workspaceId: string): Promise<boolean> {
    const { pageNumber, pageSize, orderBy, orderType } = this.store.paging;
    const request = {
      pageNumber,
      pageSize,
      orderBy,
      orderType,
      ownership: this.store.ownership,
    };

    const result = await this.wrapEnsureNotStale(async () => this.jobsApiService.getJobsAsync(workspaceId, request, this.store.jobType));

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.get_list');
      return !(result instanceof AsyncGuardError);
    }

    if (result.pagesCount < result.pageNumber) {
      if (result.pagesCount === 0) {
        this.store.jobs = [];
        this.store.paging.totalCount = 0;
        return true;
      }
      this.store.paging.pageNumber = result.pagesCount;
      return await this.getJobs(workspaceId);
    } else {
      switch (this.store.jobType) {
        case JOB_TYPE.TRAINING:
          this.store.jobs = result.data.map((dto) => this.mapToTraining(dto as ITrainingListItemDto));
          break;
        case JOB_TYPE.INFERENCE:
          this.store.jobs = result.data.map((dto) => this.mapToInference(dto as IInferenceListItemDto));
          break;
      }

      this.store.paging = {
        ...this.store.paging,
        orderBy,
        orderType,
        pageNumber: result.pageNumber,
        pagesCount: result.pagesCount,
        totalCount: result.totalCount,
      };
    }

    return true;
  }

  @action
  downloadJobAsync = async (jobId: string): Promise<IJobDownload | null> => {
    const { currentWorkspace } = this.currentWorkspaceStore;
    const { jobType } = this.store;
    const downloadUrlGenerator = async (): Promise<string | null> => {
      const downloadUrls = this.jobActionsApiService.getJobDownloadUrls(currentWorkspace.id, jobId, jobType);
      const token = await this.jobActionsApiService.getJobDownloadToken(currentWorkspace.id, jobId, jobType);
      if (token instanceof ACDLError) {
        return null;
      }

      return `${downloadUrls[JOB_DOWNLOAD_TYPE.MODEL]}?jwt=${token.jwt}`;
    };

    if (jobType === JOB_TYPE.INFERENCE) {
      const discrepancy = await this.wrapEnsureNotStale(async () => this.jobDetailsApiService.getJobThresholdDiscrepancy(currentWorkspace.id, jobId, jobType));
      if (discrepancy instanceof ACDLError) {
        return null;
      }

      if (discrepancy !== null) {
        return {
          downloadType: JOB_DOWNLOAD_TYPE.MODEL,
          urlGenerator: downloadUrlGenerator,
          baseThresholdRange: [discrepancy.base_min_threshold, discrepancy.base_max_threshold],
          currentThresholdRange: [discrepancy.current_min_threshold, discrepancy.current_max_threshold],
        };
      }
    }

    const downloadUrl = await downloadUrlGenerator();
    if (downloadUrl) {
      await downloadFileWithCheck(downloadUrl);
    }
    return null;
  };

  @action.bound
  async restartJobAsync(jobId: string): Promise<void> {
    const { currentWorkspace } = this.currentWorkspaceStore;

    const result = await this.wrapEnsureNotStale(async () => this.jobActionsApiService.postJobRestartAsync(currentWorkspace.id, jobId, this.store.jobType));
    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.restart');
      return;
    }

    await this.notifyAboutApiSuccess('fetching.success.restart');
    await this.refreshAsync();
  }

  @action.bound
  async stopJobAsync(jobId: string): Promise<void> {
    const { currentWorkspace } = this.currentWorkspaceStore;

    const result = await this.wrapEnsureNotStale(async () => this.jobActionsApiService.postJobStopAsync(currentWorkspace.id, jobId, this.store.jobType));

    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.stop');
      return;
    }

    await this.notifyAboutApiSuccess('fetching.success.stop');
    await this.refreshAsync();
  }

  @action.bound
  async deleteJobAsync(jobId: string): Promise<void> {
    const { currentWorkspace } = this.currentWorkspaceStore;

    const result = await this.wrapEnsureNotStale(async () => this.jobActionsApiService.deleteJobAsync(currentWorkspace.id, jobId, this.store.jobType));
    if (result instanceof ACDLError) {
      await this.notifyAboutApiError(result, 'fetching.failed.delete');
      return;
    }

    await this.notifyAboutApiSuccess('fetching.success.delete');
    await this.refreshAsync();
  }

  @action
  addImportError = (errorCode: string): void => {
    const { errors } = this.store;

    errors.import.set(errorCode.toLocaleLowerCase(), {
      message: `errors.${errorCode}` as TranslationKey,
    });

    errors.showImport = true;

    this.store.errors = { ...errors };
  };

  getImportErrors = (): Map<string, InputStatus> => {
    const { errors } = this.store;
    const importErrors: Map<string, InputStatus> = new Map<string, InputStatus>();

    errors.import.forEach((value, name) => {
      if (value === null) {
        return;
      }

      importErrors.set(name, InputStatus.buildFrom([value.message]));
    });

    return importErrors;
  };

  @action.bound
  clearImportErrors(): void {
    const { errors } = this.store;

    errors.import.clear();
    errors.showImport = false;

    this.store.errors = { ...errors };
  }

  @action.bound
  async importModelsAsync(name: string, description: string, file: File): Promise<boolean> {
    const { currentWorkspace } = this.currentWorkspaceStore;

    if (!currentWorkspace.id) {
      return false;
    }

    const formData = new FormData();
    formData.append('uploaded_model_name', name);
    formData.append('uploaded_model_description', description);
    formData.append('uploaded_model_file', file);

    const result = await this.wrapEnsureNotStale(async () => this.jobActionsApiService.postJobImportAsync(currentWorkspace.id, formData));
    if (result instanceof ACDLError) {
      if (result.hasErrorCodes() && result.withCode(IMPORT_ERROR_CODES)) {
        result.apiErrorResponse?.errorCodes?.forEach(this.addImportError);
      } else {
        await this.notifyAboutApiError(result, 'fetching.failed.import');
      }
      return false;
    }

    await this.refreshAsync();
    return true;
  }

  private mapToInference(data: IInferenceListItemDto): IInferenceListItem {
    return {
      id: data.inference_job_id,
      inferenceName: data.inference_name,
      trainingName: data.model_name,
      trainingVariant: data.model_variant,
      projectName: data.project_name,
      date: data.date_created,
      trainingId: data.training_job_id,
      isOwner: data.is_owner,
      isTrainingOwner: data.is_training_owner,
      score: data.score,
      status: data.status === JOB_STATUS.FAILING ? JOB_STATUS.STOPPING : data.status,
      startingProgress: data.starting_progress,
      failureReason: data.failure_reason,
      costCalculated: data.cost_calculated,
      jobType: JOB_TYPE.INFERENCE,
    };
  }

  private mapToTraining(data: ITrainingListItemDto): ITrainingListItem {
    return {
      id: data.job_id,
      trainingName: data.model_name,
      trainingVariant: data.model_variant,
      projectName: data.project_name,
      date: data.date_created,
      trainingTime: data.training_time,
      progress: data.progress,
      score: data.score,
      status: data.status === JOB_STATUS.FAILING ? JOB_STATUS.STOPPING : data.status,
      startingProgress: data.starting_progress,
      isOwner: data.is_owner,
      failureReason: data.failure_reason,
      costCalculated: data.cost_calculated,
      trainingType: data.is_auto_training ? TRAINING_TYPE.AUTO : TRAINING_TYPE.MANUAL,
      jobType: JOB_TYPE.TRAINING,
    };
  }
}
